import React, { useEffect, useState } from "react";
import AdminSidebar from "../../component/AdminComp/AdminSidebar";
import AdminTopbar from "../../component/AdminComp/AdminTopbar";
import axios from "axios";
import { baseurl, imageUrl } from "../../Baseurl";
import * as XLSX from "xlsx";
import jsPDF from "jspdf";
import "jspdf-autotable";

const DownloadApproved = () => {
  const [selectedColumns, setSelectedColumns] = useState([]);
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [exportingExcel, setExportingExcel] = useState(false);
  const [exportingPDF, setExportingPDF] = useState(false);

  const allowedColumns = [
    "id","listing_unique_id","title","condition","type","price","currency","quantity",
    "unlimitedquantity","description","created_at","updated_at","seller_id","slug",
    "mrp","unitquantity","mpn","manufacturer","listingstandard","warranty","return_policy",
    "delivery_charges","status","gst","delivery_method","standard_delivery_charge",
    "origin_pincode","distance_based_charges","delivery_time","installation_type",
    "installation_charge","delivery_notes","hsn","availableat","weight","product_type",
    "qkid","sale_type","images","modal_number",
  ];

  useEffect(() => {
    const fetchListings = async () => {
      try {
        const response = await axios.get(`${baseurl}getFullListings`);
        if (response.data?.listings?.length > 0) {
          setData(response.data.listings);
        }
      } catch (err) {
        console.error("❌ Error fetching listings:", err);
      } finally {
        setLoading(false);
      }
    };
    fetchListings();
  }, []);

  const toggleColumn = (column) => {
    setSelectedColumns((prev) =>
      prev.includes(column) ? prev.filter((c) => c !== column) : [...prev, column]
    );
  };

  // Excel Export
const handleDownloadExcel = () => {
  setExportingExcel(true);

  try {
    const rows = data.map((item) => {
      const row = {};

      selectedColumns.forEach((key) => {
        if (key === "images") {
          const imgs = item.images?.map(
            (img) => `${imageUrl}listing/${img}`
          ) || [];

          // Show up to 3 image previews
          imgs.slice(0, 3).forEach((url, index) => {
            row[`image_${index + 1}`] = {
              f: `IMAGE("${url}")`
            };
          });
        } else if (key === "modal_number") {
          row[key] = item.modal_number ?? "";
        } else {
          row[key] = item[key] ?? "";
        }
      });

      return row;
    });

    const ws = XLSX.utils.json_to_sheet(rows);

    // Improve column width for images
    ws["!cols"] = Object.keys(rows[0] || {}).map((key) => ({
      wch: key.startsWith("image_") ? 25 : 18,
    }));

    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "Listings");
    XLSX.writeFile(wb, "listings_with_image_preview.xlsx");
  } catch (err) {
    console.error("❌ Excel export failed:", err);
  } finally {
    setExportingExcel(false);
  }
};


  // PDF Export with image previews
  const handleDownloadPDF = async () => {
    setExportingPDF(true);
    try {
      const doc = new jsPDF("l", "pt", "a4"); // landscape
      const margin = 10;
      const cellPadding = 5;
      const rowHeight = 60;
      const tableColumn = selectedColumns.map(col => col.toUpperCase());
      const tableRows = [];

      for (const item of data) {
        const rowData = [];
        for (const key of selectedColumns) {
          if (key === "images") {
            const imgs = item.images?.map((img) => `${imageUrl}listing/${img}`) || [];
            const validImgs = imgs.filter(url => /\.(jpg|jpeg|png|webp)$/i.test(url));
            rowData.push(validImgs);
          } else if (key === "modal_number") {
            rowData.push(item.modal_number ?? "");
          } else {
            rowData.push(item[key] ?? "");
          }
        }
        tableRows.push(rowData);
      }

      let startY = margin;
      const pageWidth = doc.internal.pageSize.getWidth();
      const colWidth = pageWidth / selectedColumns.length;

      tableColumn.forEach((col, i) => {
        doc.text(col, margin + i * colWidth + cellPadding, startY + 10);
      });
      startY += rowHeight / 2;

      for (const row of tableRows) {
        let maxRowHeight = rowHeight;
        for (let i = 0; i < row.length; i++) {
          const cell = row[i];
          const x = margin + i * colWidth;
          const y = startY;

          if (Array.isArray(cell)) {
            let imgX = x + cellPadding;
            let imgY = y;
            const imgSize = 40;
            for (const imgUrl of cell) {
              try {
                const img = await loadImageToDataURL(imgUrl);
                doc.addImage(img, "JPEG", imgX, imgY, imgSize, imgSize);
                imgX += imgSize + 5;
              } catch (err) {
                console.warn("Image failed to load:", imgUrl, err);
              }
            }
          } else {
            doc.text(String(cell), x + cellPadding, y + 15);
          }
        }
        startY += maxRowHeight;
        if (startY + rowHeight > doc.internal.pageSize.getHeight() - margin) {
          doc.addPage();
          startY = margin;
        }
      }

      doc.save("selected_columns_listings.pdf");
    } catch (err) {
      console.error("❌ PDF export failed:", err);
    } finally {
      setExportingPDF(false);
    }
  };

  const loadImageToDataURL = (url) => {
    return new Promise((resolve, reject) => {
      const img = new Image();
      img.crossOrigin = "Anonymous";
      img.onload = () => {
        const canvas = document.createElement("canvas");
        canvas.width = img.width;
        canvas.height = img.height;
        const ctx = canvas.getContext("2d");
        ctx.drawImage(img, 0, 0);
        resolve(canvas.toDataURL("image/jpeg"));
      };
      img.onerror = (err) => {
        console.error("Image load error:", url, err);
        reject(err);
      };
      img.src = url;
    });
  };

  return (
    <div className="App" style={{ display: "flex" }}>
      <AdminSidebar />
      <div className="content" style={{ flexGrow: 1, padding: "20px" }}>
        <AdminTopbar />
        <div className="container-fluid">
          <div className="card text-start">
            <div className="card-body">
              <h4>Select Columns to Export</h4>
              {loading ? (
                <p>Loading...</p>
              ) : (
                <>
                  <div className="row">
                    {allowedColumns.map((col) => (
                      <div key={col} className="col-md-3 mb-2">
                        <label>
                          <input
                            type="checkbox"
                            value={col}
                            onChange={() => toggleColumn(col)}
                            checked={selectedColumns.includes(col)}
                            className="me-2"
                          />
                          {col}
                        </label>
                      </div>
                    ))}
                  </div>

                  {selectedColumns.length > 0 && (
                    <div className="mt-3">
                      <button
                        className="btn btn-success me-2"
                        onClick={handleDownloadExcel}
                        disabled={exportingExcel}
                      >
                        {exportingExcel ? "Exporting Excel..." : "Download Excel"}
                      </button>
{/* 
                      <button
                        className="btn btn-primary"
                        onClick={handleDownloadPDF}
                        disabled={exportingPDF}
                      >
                        {exportingPDF ? "Exporting PDF..." : "Download PDF"}
                      </button> */}
                    </div>
                  )}
                </>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default DownloadApproved;
